// ============================================================================
// TYPES AND INTERFACES
// ============================================================================

export interface TimerDebugEvent {
  timestamp: number;
  event: string;
  sessionId?: string;
  details: any;
  stackTrace?: string;
}

export interface TimerDebugState {
  events: TimerDebugEvent[];
  currentSession: string | null;
  timerState: any;
  issues: string[];
  performance: TimerDebugPerformance;
}

export interface TimerDebugPerformance {
  renderCount: number;
  lastRenderTime: number;
  averageRenderTime: number;
}

export interface StateChange {
  key: string;
  from: any;
  to: any;
}

export interface DebugExportData {
  timestamp: string;
  debugState: TimerDebugState;
  userAgent: string;
  url: string;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const TIMER_DEBUG_CONSTANTS = {
  PERFORMANCE: {
    SLOW_RENDER_THRESHOLD: 16, // ms (60fps = ~16.67ms per frame)
    MAX_EVENTS: 100,
    RAPID_STATE_CHANGE_THRESHOLD: 10, // events per second
    TIMER_RESET_THRESHOLD: 5000, // ms
    LARGE_TIME_JUMP_THRESHOLD: 10, // seconds
    EXCESSIVE_STATE_CHANGES_THRESHOLD: 10
  },
  LOGGING: {
    EMOJI: {
      ENABLE: '🔧',
      EVENT: '🔍',
      ISSUE: '⚠️',
      CLEAR: '🧹',
      EXPORT: '📊',
      SESSION: '🔄'
    },
    PREFIX: 'TimerDebugger'
  },
  EVENTS: {
    STATE_CHANGE: 'STATE_CHANGE',
    RENDER: 'RENDER',
    SESSION_CHANGE: 'SESSION_CHANGE',
    CONSOLE_ERROR: 'CONSOLE_ERROR',
    WINDOW_ERROR: 'WINDOW_ERROR'
  },
  TIMER_OPERATIONS: [
    'START', 'PAUSE', 'RESUME', 'STOP', 'RESET', 'DESTROY'
  ]
} as const;

// ============================================================================
// TIMER DEBUGGER CLASS
// ============================================================================

/**
 * Comprehensive debugging and monitoring system for timer issues
 * Provides real-time monitoring, issue detection, and performance tracking
 */
class TimerDebugger {
  private events: TimerDebugEvent[] = [];
  private currentSession: string | null = null;
  private renderCount = 0;
  private lastRenderTime = 0;
  private totalRenderTime = 0;
  private issues: string[] = [];
  private isEnabled = false; // Temporarily disabled to reduce console spam
  private originalConsoleError: typeof console.error;

  constructor() {
    this.originalConsoleError = console.error;
    this.setupGlobalMonitoring();
  }

  // ============================================================================
  // PUBLIC API METHODS
  // ============================================================================

  /**
   * Enable or disable debugging functionality
   * @param enabled - Whether to enable debugging
   */
  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
    this.log(`Debugging ${enabled ? 'Enabled' : 'Disabled'}`, { enabled });
  }

  /**
   * Log a timer event with optional session context and details
   * @param event - Event name to log
   * @param sessionId - Optional session identifier
   * @param details - Optional event details
   */
  logEvent(event: string, sessionId?: string, details?: any): void {
    if (!this.isEnabled) return;

    const debugEvent: TimerDebugEvent = {
      timestamp: Date.now(),
      event,
      sessionId,
      details,
      stackTrace: new Error().stack
    };

    this.addEvent(debugEvent);
    this.log(`Event: ${event}`, { sessionId, details, timestamp: debugEvent.timestamp });
    this.detectIssues(debugEvent);
  }

  /**
   * Monitor timer state changes and detect anomalies
   * @param oldState - Previous timer state
   * @param newState - Current timer state
   * @param sessionId - Optional session identifier
   */
  monitorStateChange(oldState: any, newState: any, sessionId?: string): void {
    if (!this.isEnabled) return;

    const changes = this.getStateChanges(oldState, newState);
    const significantChanges = this.filterSignificantChanges(changes);
    
    if (significantChanges.length > 0) {
      this.logEvent(TIMER_DEBUG_CONSTANTS.EVENTS.STATE_CHANGE, sessionId, {
        changes: significantChanges,
        oldState: this.sanitizeState(oldState),
        newState: this.sanitizeState(newState)
      });
    }

    this.checkStateAnomalies(oldState, newState);
  }

  /**
   * Monitor render performance for components
   * @param componentName - Name of the component being rendered
   * @param renderTime - Time taken for rendering in milliseconds
   */
  monitorRender(componentName: string, renderTime: number): void {
    if (!this.isEnabled) return;

    this.updateRenderMetrics(renderTime);

    this.logEvent(TIMER_DEBUG_CONSTANTS.EVENTS.RENDER, undefined, {
      component: componentName,
      renderTime,
      averageRenderTime: this.getAverageRenderTime(),
      renderCount: this.renderCount
    });

    this.checkRenderPerformance(renderTime, componentName);
  }

  /**
   * Monitor timer operations and track session changes
   * @param operation - Timer operation being performed
   * @param sessionId - Optional session identifier
   * @param details - Optional operation details
   */
  monitorTimerOperation(operation: string, sessionId?: string, details?: any): void {
    if (!this.isEnabled) return;

    this.logEvent(`TIMER_${operation.toUpperCase()}`, sessionId, details);
    this.trackSessionChanges(sessionId);
  }

  /**
   * Get current debug state for external consumption
   * @returns Current debug state object
   */
  getDebugState(): TimerDebugState {
    return {
      events: [...this.events],
      currentSession: this.currentSession,
      timerState: null, // Will be set by caller
      issues: [...this.issues],
      performance: this.getPerformanceMetrics()
    };
  }

  /**
   * Clear all debug data and reset counters
   */
  clear(): void {
    this.events = [];
    this.issues = [];
    this.renderCount = 0;
    this.totalRenderTime = 0;
    this.lastRenderTime = 0;
    this.log('Cleared all debug data', { action: 'clear' });
  }

  /**
   * Export debug data for analysis
   * @returns Formatted debug export data
   */
  exportDebugData(): DebugExportData {
    const data: DebugExportData = {
      timestamp: new Date().toISOString(),
      debugState: this.getDebugState(),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'Unknown',
      url: typeof window !== 'undefined' ? window.location.href : 'Unknown'
    };

    this.log('Debug data exported', data);
    return data;
  }

  // ============================================================================
  // PRIVATE HELPER METHODS
  // ============================================================================

  /**
   * Add event to internal storage with size management
   */
  private addEvent(event: TimerDebugEvent): void {
    this.events.push(event);
    
    // Keep only last N events to prevent memory issues
    if (this.events.length > TIMER_DEBUG_CONSTANTS.PERFORMANCE.MAX_EVENTS) {
      this.events = this.events.slice(-TIMER_DEBUG_CONSTANTS.PERFORMANCE.MAX_EVENTS);
    }
  }

  /**
   * Update render performance metrics
   */
  private updateRenderMetrics(renderTime: number): void {
    this.renderCount++;
    this.lastRenderTime = renderTime;
    this.totalRenderTime += renderTime;
  }

  /**
   * Get average render time
   */
  private getAverageRenderTime(): number {
    return this.renderCount > 0 ? this.totalRenderTime / this.renderCount : 0;
  }

  /**
   * Get performance metrics object
   */
  private getPerformanceMetrics(): TimerDebugPerformance {
    return {
      renderCount: this.renderCount,
      lastRenderTime: this.lastRenderTime,
      averageRenderTime: this.getAverageRenderTime()
    };
  }

  /**
   * Track session changes and log when sessions switch
   */
  private trackSessionChanges(sessionId?: string): void {
    if (sessionId && sessionId !== this.currentSession) {
      const previousSession = this.currentSession;
      this.currentSession = sessionId;
      
      this.logEvent(TIMER_DEBUG_CONSTANTS.EVENTS.SESSION_CHANGE, sessionId, { 
        previousSession 
      });
    }
  }

  /**
   * Filter state changes to only significant ones
   */
  private filterSignificantChanges(changes: Record<string, any>): StateChange[] {
    const significantKeys = ['isRunning', 'isPaused', 'remainingTime', 'progress'];
    
    return Object.entries(changes)
      .filter(([key]) => significantKeys.includes(key))
      .map(([key, value]) => ({
        key,
        from: value.from,
        to: value.to
      }));
  }

  /**
   * Log messages with consistent formatting
   */
  private log(message: string, details?: any): void {
    const emoji = this.getLogEmoji(message);
    const prefix = TIMER_DEBUG_CONSTANTS.LOGGING.PREFIX;
    
    console.log(`${emoji} ${prefix}: ${message}`, details);
  }

  /**
   * Get appropriate emoji for logging
   */
  private getLogEmoji(message: string): string {
    const emojiMap: Record<string, string> = {
      'Debugging Enabled': TIMER_DEBUG_CONSTANTS.LOGGING.EMOJI.ENABLE,
      'Debugging Disabled': TIMER_DEBUG_CONSTANTS.LOGGING.EMOJI.ENABLE,
      'Cleared all debug data': TIMER_DEBUG_CONSTANTS.LOGGING.EMOJI.CLEAR,
      'Debug data exported': TIMER_DEBUG_CONSTANTS.LOGGING.EMOJI.EXPORT
    };

    return emojiMap[message] || TIMER_DEBUG_CONSTANTS.LOGGING.EMOJI.EVENT;
  }

  // ============================================================================
  // ISSUE DETECTION METHODS
  // ============================================================================

  /**
   * Detect potential issues based on event patterns
   */
  private detectIssues(event: TimerDebugEvent): void {
    const { event: eventType, details } = event;

    this.checkRapidStateChanges();
    this.checkTimerResets(eventType);
    this.checkPauseResumeIssues(eventType);
  }

  /**
   * Check for excessive state changes
   */
  private checkRapidStateChanges(): void {
    const recentEvents = this.events.filter(e => 
      e.timestamp > Date.now() - 1000 && e.event === TIMER_DEBUG_CONSTANTS.EVENTS.STATE_CHANGE
    );

    if (recentEvents.length > TIMER_DEBUG_CONSTANTS.PERFORMANCE.EXCESSIVE_STATE_CHANGES_THRESHOLD) {
      this.addIssue(`Excessive state changes: ${recentEvents.length} in 1 second`);
    }
  }

  /**
   * Check for timer reset patterns
   */
  private checkTimerResets(eventType: string): void {
    if (eventType === 'TIMER_START' && this.currentSession) {
      const lastStart = this.events.filter(e => 
        e.event === 'TIMER_START' && e.sessionId === this.currentSession
      ).pop();
      
      if (lastStart && Date.now() - lastStart.timestamp < TIMER_DEBUG_CONSTANTS.PERFORMANCE.TIMER_RESET_THRESHOLD) {
        this.addIssue(`Timer reset detected for session ${this.currentSession}`);
      }
    }
  }

  /**
   * Check for pause/resume sequence issues
   */
  private checkPauseResumeIssues(eventType: string): void {
    if (eventType === 'TIMER_PAUSE' || eventType === 'TIMER_RESUME') {
      const lastPause = this.events.filter(e => e.event === 'TIMER_PAUSE').pop();
      const lastResume = this.events.filter(e => e.event === 'TIMER_RESUME').pop();
      
      if (lastPause && lastResume && lastResume.timestamp < lastPause.timestamp) {
        this.addIssue('Resume called before pause - potential state corruption');
      }
    }
  }

  /**
   * Check for state anomalies and impossible combinations
   */
  private checkStateAnomalies(oldState: any, newState: any): void {
    this.checkImpossibleStates(newState);
    this.checkTimeAnomalies(oldState, newState);
  }

  /**
   * Check for impossible state combinations
   */
  private checkImpossibleStates(newState: any): void {
    if (newState.isRunning && newState.isPaused) {
      this.addIssue('Invalid state: timer is both running and paused');
    }
  }

  /**
   * Check for time-related anomalies
   */
  private checkTimeAnomalies(oldState: any, newState: any): void {
    if (oldState.remainingTime && newState.remainingTime) {
      // Check for time going backwards
      if (newState.remainingTime > oldState.remainingTime) {
        this.addIssue('Time went backwards - potential timer reset');
      }

      // Check for excessive time jumps
      const timeDiff = Math.abs(newState.remainingTime - oldState.remainingTime);
      if (timeDiff > TIMER_DEBUG_CONSTANTS.PERFORMANCE.LARGE_TIME_JUMP_THRESHOLD) {
        this.addIssue(`Large time jump detected: ${timeDiff} seconds`);
      }
    }
  }

  /**
   * Check render performance and warn if too slow
   */
  private checkRenderPerformance(renderTime: number, componentName: string): void {
    if (renderTime > TIMER_DEBUG_CONSTANTS.PERFORMANCE.SLOW_RENDER_THRESHOLD) {
      this.addIssue(`Slow render detected: ${componentName} took ${renderTime}ms`);
    }
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  /**
   * Get state changes between two states
   */
  private getStateChanges(oldState: any, newState: any): Record<string, any> {
    const changes: Record<string, any> = {};
    
    for (const key in newState) {
      if (oldState[key] !== newState[key]) {
        changes[key] = {
          from: oldState[key],
          to: newState[key]
        };
      }
    }
    
    return changes;
  }

  /**
   * Sanitize state for logging by removing sensitive data
   */
  private sanitizeState(state: any): any {
    if (!state) return null;
    
    const sanitized = { ...state };
    
    // Remove sensitive or large data
    delete sanitized.stackTrace;
    delete sanitized.details;
    
    return sanitized;
  }

  /**
   * Add an issue to the issues list if not already present
   */
  private addIssue(issue: string): void {
    if (!this.issues.includes(issue)) {
      this.issues.push(issue);
      console.warn(`${TIMER_DEBUG_CONSTANTS.LOGGING.EMOJI.ISSUE} TimerDebugger Issue: ${issue}`);
    }
  }

  // ============================================================================
  // GLOBAL MONITORING SETUP
  // ============================================================================

  /**
   * Setup global monitoring for console errors and window errors
   */
  private setupGlobalMonitoring(): void {
    this.setupConsoleErrorMonitoring();
    this.setupWindowErrorMonitoring();
  }

  /**
   * Monitor console errors related to timers
   */
  private setupConsoleErrorMonitoring(): void {
    console.error = (...args) => {
      const message = args.join(' ');
      if (message.toLowerCase().includes('timer')) {
        this.logEvent(TIMER_DEBUG_CONSTANTS.EVENTS.CONSOLE_ERROR, undefined, { message });
      }
      this.originalConsoleError.apply(console, args);
    };
  }

  /**
   * Monitor window errors related to timers
   */
  private setupWindowErrorMonitoring(): void {
    if (typeof window !== 'undefined') {
      window.addEventListener('error', (event) => {
        if (event.message.toLowerCase().includes('timer')) {
          this.logEvent(TIMER_DEBUG_CONSTANTS.EVENTS.WINDOW_ERROR, undefined, { 
            message: event.message,
            filename: event.filename,
            lineno: event.lineno,
            timeStamp: event.timeStamp
          });
        }
      });
    }
  }
}

// ============================================================================
// EXPORTED INSTANCE
// ============================================================================

/**
 * Global timer debugger instance
 */
export const timerDebugger = new TimerDebugger();

// ============================================================================
// GLOBAL EXPOSURE FOR MANUAL DEBUGGING
// ============================================================================

/**
 * Expose debugger to window for manual debugging in development
 */
if (typeof window !== 'undefined') {
  (window as any).timerDebugger = timerDebugger;
  console.log('🔧 TimerDebugger available as window.timerDebugger');
}

// ============================================================================
// CONVENIENCE FUNCTIONS
// ============================================================================

/**
 * Quick function to enable/disable debugging
 * @param enabled - Whether to enable debugging
 */
export const setTimerDebugging = (enabled: boolean): void => {
  timerDebugger.setEnabled(enabled);
};

/**
 * Quick function to log a timer event
 * @param event - Event name
 * @param sessionId - Optional session ID
 * @param details - Optional event details
 */
export const logTimerEvent = (event: string, sessionId?: string, details?: any): void => {
  timerDebugger.logEvent(event, sessionId, details);
};

/**
 * Quick function to get debug state
 * @returns Current debug state
 */
export const getTimerDebugState = (): TimerDebugState => {
  return timerDebugger.getDebugState();
}; 